import { useEffect } from 'react';

interface ShortcutHandlers {
  /** ⌘K / Ctrl+K — start a new chat */
  onNewChat?: () => void;
  /** ⌘B / Ctrl+B — toggle the projects sidebar */
  onToggleSidebar?: () => void;
  /** ⌘. / Ctrl+. — toggle the artifacts side panel */
  onToggleArtifacts?: () => void;
  /** Escape — close the artifacts panel if it's open */
  onEscape?: () => void;
}

/**
 * Wire app-wide keyboard shortcuts.
 *
 * - Ignores key events while the user is typing in an input / textarea /
 *   contentEditable element, so typing the letter "k" in the composer
 *   doesn't fire a new-chat shortcut.
 * - Honours both ⌘ (Cmd, macOS) and Ctrl (Linux/Windows). Shift / alt
 *   modifiers disable the shortcut to avoid stealing browser bindings.
 */
export function useGlobalShortcuts({
  onNewChat,
  onToggleSidebar,
  onToggleArtifacts,
  onEscape,
}: ShortcutHandlers): void {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Escape always wins — it's the universal "get me out".
      if (e.key === 'Escape') {
        onEscape?.();
        return;
      }

      const hasModifier = e.metaKey || e.ctrlKey;
      if (!hasModifier || e.shiftKey || e.altKey) return;

      // Don't capture shortcuts while the user is typing.
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) {
          return;
        }
      }

      const key = e.key.toLowerCase();
      if (key === 'k' && onNewChat) {
        e.preventDefault();
        onNewChat();
      } else if (key === 'b' && onToggleSidebar) {
        e.preventDefault();
        onToggleSidebar();
      } else if ((key === '.' || key === 'j') && onToggleArtifacts) {
        // ⌘J is a backup mapping since some users have ⌘. bound elsewhere
        // (e.g. macOS' system cancel). Both fire the same toggle.
        e.preventDefault();
        onToggleArtifacts();
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onNewChat, onToggleSidebar, onToggleArtifacts, onEscape]);
}
